/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 대화 블록을 정의한다.
 *
 * @file /src/rootin/blocks/talk/Talk.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 15.
 */
import { Block } from '@rootin/base/Block';
import { BlockClass } from '@rootin/base/Blocks';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Balloon } from './blocks/Balloon';
import { Me } from './tools/Me';
import { You } from './tools/You';
import { Container } from '@rootin/elements/container/Container';
import { Dom } from '@utils/Dom';
import { Language, LanguageText } from '@utils/Language';
import './Talk.css';

export class Talk extends Block {
    /**
     * 블록타입
     */
    static type = 'talk';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'atomic';

    /**
     * 블록아이콘
     */
    static icon: string =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-messages-square"><path d="M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/><path d="M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1"/></svg>';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.talk.label');

    /**
     * 말풍선 컨테이너
     */
    balloons: Container;

    /**
     * 대화 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     */
    constructor(editor: Editor) {
        super(editor);
        this.balloons = new Container(this, {
            name: 'balloons',
            blocks: [Balloon.type],
            insertTarget: false,
            defaultBlock: () => new Balloon(this.editor),
        });
        this.$getBlock().append(this.balloons.$getElement());
    }

    /**
     * 블록을 초기화한다. 말풍선을 나의 대화/상대방의 대화로 설정하는 툴은 대화블록 안에서만
     * 쓰이므로(테이블의 병합/분할 툴과 같은 방식), 전역 툴 목록이 아니라 이 블록이 초기화될 때
     * 직접 등록한다 — 각 툴의 `visible()`이 대화블록의 말풍선에 포커스가 있을 때만 노출한다.
     *
     * @param editor - 블록이 추가되는 에디터
     * @returns 블록추가 버튼 DOM
     */
    static init(editor: Editor): Dom {
        editor.addTool(new You());
        editor.addTool(new Me());

        return super.init(editor);
    }

    /**
     * 타입 문자열로 블록 클래스를 가져온다. 말풍선 블록타입은 이 블록만 알고 있으므로 직접
     * 처리하고, 그 외에는 상위 구현에 위임한다.
     *
     * @param type - 블록타입
     * @returns 해당 블록 클래스
     */
    getBlockClass(type: string): BlockClass | null {
        return type === Balloon.type ? (Balloon as BlockClass) : super.getBlockClass(type);
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        await this.balloons.render();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.balloons.isEmpty();
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        return { id: this.getId(), type: this.getType(), balloons: this.balloons.getValue() };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        await this.balloons.setValue(value.balloons);
    }

    /**
     * 블록의 HTML 을 받아, 블록값을 지정한다.
     *
     * @param html - 블록 HTML
     */
    async setValueFromHtml(html: string): Promise<void> {
        const dom = document.createElement('div');
        dom.innerHTML = html;

        const $rootinBlock = dom.firstElementChild;
        if (
            $rootinBlock === null ||
            $rootinBlock.tagName !== 'ROOTIN-BLOCK' ||
            $rootinBlock.getAttribute('data-type') !== this.getType()
        ) {
            return;
        }

        const $balloons = Array.from($rootinBlock.children).find(
            (el) => el.tagName === 'ROOTIN-ELEMENT' && el.getAttribute('data-type') === 'container'
        );
        if ($balloons !== undefined) {
            await this.balloons.setValueFromHtml($balloons);
        }
    }

    /**
     * 현재 블록내 선택영역이 존재할 경우, 선택영역을 텍스트로 추출하고 블록에서 제거한다.
     *
     * @param is_all - 선택영역 부분추출이 아닌 전체를 추출할지 여부
     * @returns 추출한 텍스트 목록과, 선택 이후 남은영역이 포함된 블록(없는 경우 NULL)
     */
    async extractSelection(is_all: boolean): Promise<{ texts: string[]; remain: Block | null }> {
        const range = this.editor.selection.getRange();
        if (range === null || range.collapsed === true) {
            return { texts: [], remain: null };
        }

        // 드래그로 블록 전체가 잠긴(통째로 선택된) 상태라면, 말풍선별 판단 없이 전체를 넘긴다.
        const isLocked = this.$getBlock().getAttr('data-selected') === 'true';
        if (isLocked === false && range.intersectsNode(this.$getBlock().getEl()) === false) {
            return { texts: [], remain: null };
        }

        const balloons = this.balloons.getBlocks();
        const texts: string[] = [];

        // 대화는 말풍선 단위로만 통째로 넘길 수 있으므로(부분 말풍선 추출 불가), 아래 두 분기
        // 모두 is_all 값과 무관하게 항상 balloon.extractSelection(true) 로 말풍선 전체를 추출한다.
        if (isLocked === true) {
            for (const balloon of balloons) {
                texts.push(...(await balloon.extractSelection(true)).texts);
            }

            if (this.isEmpty() === true) {
                await this.getParent()?.removeBlock(this);
            }

            return { texts, remain: null };
        }

        // 말풍선의 "블록 전체"가 선택영역과 겹치는지가 아니라, 실제로 그 말풍선이 텍스트를
        // 내어줬는지(=자기 판단으로 진짜 선택이 닿았는지)로 앞/뒤를 가른다.
        const after: Block[] = [];
        let phase: 'before' | 'selected' | 'after' = 'before';

        for (const balloon of balloons) {
            const extracted = await balloon.extractSelection(true);
            if (extracted.texts.length > 0) {
                phase = 'selected';
                texts.push(...extracted.texts);
            } else if (phase !== 'before') {
                phase = 'after';
                after.push(balloon);
            }
        }

        let remain: Block | null = null;
        if (after.length > 0) {
            const afterTalk = this.createRemainBlock();
            for (const balloon of after) {
                afterTalk.balloons.$getElement().getEl().appendChild(balloon.$getBlock().getEl());
            }
            remain = afterTalk;
        }

        if (this.isEmpty() === true) {
            await this.getParent()?.removeBlock(this);
        }

        return { texts, remain };
    }

    /**
     * 선택영역 추출 후 뒤에 남는 말풍선들을 담을 새 대화 블록을 만든다.
     *
     * @returns 새 블록 인스턴스
     */
    private createRemainBlock(): Talk {
        return new Talk(this.editor);
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {
        const range = this.editor.selection.getRange();
        if (range !== null && range.collapsed === false) {
            const container = this.getParent();
            if (container !== null) {
                const texts: string[] = [];
                let remain: Block | null = null;

                for (const sibling of container.getBlocks()) {
                    if (sibling === this) {
                        continue;
                    }

                    const extracted = await sibling.extractSelection(false);
                    texts.push(...extracted.texts);
                    if (extracted.remain !== null) {
                        remain = extracted.remain;
                    }
                }

                // 형제로부터 받은 텍스트 각각을 줄바꿈(<br>) 기준으로 나눠 말풍선 하나씩 채운다.
                // 빈 줄은 말풍선으로 만들지 않는다.
                const lines = texts.flatMap((text) => text.split('<br>')).filter((line) => line.trim() !== '');
                if (lines.length > 0) {
                    const [firstBalloon] = this.balloons.getBlocks() as Balloon[];

                    for (let index = 0; index < lines.length; index++) {
                        let balloon = index === 0 ? firstBalloon : null;
                        if (!balloon) {
                            balloon = new Balloon(this.editor, {
                                side: index % 2 === 0 ? 'me' : 'you',
                            });
                            this.balloons.$getElement().append(balloon.$getBlock());
                            await balloon.render();
                        }

                        await balloon.text.setValue(lines[index]);
                    }
                }

                if (remain !== null) {
                    await container.insertBlock(remain, this.$getBlock().getEl().nextSibling);
                }
            }
        }

        this.focus(true);
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param focused - Element 인 경우 나의 블록의 포커스된 엘리먼트, `Block`인 경우 새 블록을 삽입할 기준블록
     * @returns 처리결과 (true 인 경우 모든 데이터를 처리함, 그렇지 않은 경우 처리되지 않은 데이터)
     */
    async onPaste(type: string, data: any, focused: Block | Element | null = null): Promise<true | any> {
        if (type.startsWith('text/') === false) {
            return data;
        }

        if (focused === null || focused instanceof Element) {
            return data;
        }

        if (type === 'text/html') {
            const dom = document.createElement('div');
            dom.innerHTML = data as string;

            const container = focused.getParent();
            if (container === null) {
                return data;
            }

            const $matched = dom.querySelector(`rootin-block[data-type='${this.getType()}']`);
            if ($matched !== null) {
                $matched.remove();
                const remain = dom.innerHTML === '' ? true : dom.innerHTML;

                await this.setValueFromHtml($matched.outerHTML);
                await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);

                return remain;
            }

            const $orphans = Array.from(dom.querySelectorAll(`rootin-block[data-type='${Balloon.type}']`)).filter(
                (el) => (el.parentElement?.closest('rootin-block') ?? null) === null
            );
            if ($orphans.length > 0) {
                for (const $orphan of $orphans) {
                    $orphan.remove();
                    const balloon = Balloon.create(this.editor);
                    await balloon.setValueFromHtml($orphan.outerHTML);
                    this.balloons.$getElement().append(balloon.$getBlock());
                    await balloon.render();
                }
                const remain = dom.innerHTML === '' ? true : dom.innerHTML;

                await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);

                return remain;
            }
        }

        return data;
    }
}
